/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit} from '@angular/core';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {DISMISS_MSG} from '../../../common/constants';
import {getDefaultYearRange, range} from '../../../core/helpers/generic-helpers';
import {StateService} from '../../../core/state/state.service';
import {PropulsionScenarioService} from '../../../services/propulsion-scenario-service';

@Component({
    selector: 'app-update-propulsion-scenario-with-port',
    templateUrl: './update-propulsion-scenario-with-port.component.html',
    styleUrl: './update-propulsion-scenario-with-port.component.scss',
    standalone: false
})
export class UpdatePropulsionScenarioWithPortComponent implements OnInit {
  checkboxValue  = false;
  showSpinner: boolean;

  scenarioYearRange: number[] = []
  selectedStartYear: number;
  selectedEndYear: number;

  propulsionScenarioId: number;
  vehScenId: number;

  constructor(public snackBar: FabMessageService,
              public dialogRef: MatDialogRef<UpdatePropulsionScenarioWithPortComponent>,
              public dialog: MatDialog,
              readonly propulsionScenarioService: PropulsionScenarioService,
              readonly stateService: StateService) {

  }

  ngOnInit(): void {
    this.propulsionScenarioId = this.stateService.selectedScenario.propulsionScenario.id;
    this.vehScenId = this.stateService.selectedScenario.vehScenId;
    this.getYearRange();
  }

  /**
   * Event handler for closing the dialog
   */
  onCancel(): void {
    this.dialogRef.close();
  }

  /**
   * Event handler for confirm button
   */
  onConfirm(): void {
    this.showSpinner = true;
    this.propulsionScenarioService.updatePropulsionScenarioWithPortfolio(this.propulsionScenarioId, this.vehScenId,
      this.checkboxValue, this.stateService.getUserGmid(), this.selectedStartYear, this.selectedEndYear).subscribe({
      error: err => {
        this.showSpinner = false;
        this.snackBar.displayError(err, DISMISS_MSG);
      },
      complete: () => {
        this.showSpinner = false;
        this.snackBar.displaySuccess('The propulsion scenario was successfully updated with the portfolio', DISMISS_MSG)
        this.dialogRef.close();
      }
    });
  }

  /**
   * Event handler for checkbox change event
   */
  onCheckboxChange(): void {
    if (this.checkboxValue) {
      this.getYearRange();
    }
  }

  /**
   * Gets the year range of a selected propulsion scenario.
   * When the scenario has no penetrations, MIN/MAX year are null — fall back to the default year range
   * so the portfolio update can still proceed with valid start/end years.
   */
  getYearRange(): void {
    this.showSpinner = true;
    this.propulsionScenarioService.getYearRange(this.propulsionScenarioId).subscribe(
      {
        next: (data: any[]) => {
          const scenarioMinYear = data?.[0]?.MIN_YEAR;
          const scenarioMaxYear = data?.[0]?.MAX_YEAR;
          if (scenarioMinYear == null || scenarioMaxYear == null) {
            const {yearRange, startYear, endYear} = getDefaultYearRange();
            this.selectedStartYear = startYear;
            this.selectedEndYear = endYear;
            this.scenarioYearRange = yearRange;
          } else {
            this.selectedStartYear = scenarioMinYear;
            this.selectedEndYear = scenarioMaxYear;
            this.scenarioYearRange = range(this.selectedStartYear, this.selectedEndYear + 1);
          }
        },
        error: (err) => {
          this.showSpinner = false;
          this.snackBar.displayError(err, DISMISS_MSG);
        },
        complete: () => {
          this.showSpinner = false;
        }
      }
    );
  }
}
